import * as React from 'react'
import { cn } from '@/lib/utils'

/** List cards follow available content width, including when the sidebar is open. */
function CardGrid({ className, ...props }) {
  return <div className={cn('grid grid-cols-[repeat(auto-fill,minmax(min(100%,19rem),1fr))] gap-4 lg:gap-6', className)} {...props} />
}

/**
 * The three surfaces a Card can be (design system spec §2/§4.6: "three KPI
 * variants — default tint / ink-filled hero / accent-muted-filled
 * emphasis"). `default` is the ordinary resting card; `ink` is the one
 * dark, high-emphasis chrome surface a page gets (never a second brand
 * colour — see index.css's accent-token comment); `brand` is the
 * accent-muted "workhorse fill" role.
 */
const CARD_TONES = {
  default: 'bg-surface text-ink border border-line',
  ink: 'bg-ink text-canvas',
  brand: 'bg-accent-muted text-accent-muted-fg',
}

/**
 * Tells a `CardFooter` whether its card is a full-height flex column, so the
 * footer pins with `mt-auto` only where that works; in a block-flow card
 * `mt-auto` resolves to 0 and would drop the hairline footer's 8px gap.
 */
const CardContext = React.createContext({ stretch: false })
const STRETCH_CARD = { stretch: true }
const STATIC_CARD = { stretch: false }

/**
 * Card container component.
 *
 * The default tone carries the spec's resting hairline (`border-line`, spec
 * §1 "cards lose the heavy border in favour of a hairline"): surface-vs-canvas
 * contrast alone collapses in dark mode once a tenant tints the canvas
 * toward its brand (F-13, Riverbend #2f6f4e), so the line is the guarantee.
 * `ink`/`brand` tones are self-evidently bounded and stay borderless.
 * `interactive` opts a
 * card into the mockup's one piece of motion — a hover lift with the
 * existing popover shadow — for picker-style grids where a card is itself
 * the click target. Plain content cards (dashboard widgets, anything with
 * its own inner controls) leave it off.
 *
 * An interactive card is also a full-height flex column (rulebook R3): in a
 * `CardGrid` row every card stretches to the tallest one, and a
 * `CardFooter` (which carries `mt-auto`) pins to the bottom, so footers line
 * up across the row whether or not a card has tags or a description.
 * `stretch` gives a non-interactive card that same layout (a card whose CTA
 * lives in the footer rather than the whole surface being a click target,
 * e.g. `MetricCard` - the footer meta/CTA row still needs to pin to the
 * bottom of a `CardGrid` row without the hover lift `interactive` implies).
 * `interactive` always implies `stretch`; passing both is redundant, not a
 * conflict.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {'default'|'ink'|'brand'} [props.tone='default']
 * @param {boolean} [props.interactive=false] - Hover lift + shadow; the card itself is a click target
 * @param {boolean} [props.stretch=false] - Full-height flex column + `CardFooter` `mt-auto`, without the hover lift
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const Card = React.forwardRef(({ className, tone = 'default', interactive = false, stretch = false, ...props }, ref) => {
  const isStretch = interactive || stretch
  return (
    <CardContext.Provider value={isStretch ? STRETCH_CARD : STATIC_CARD}>
      <div
        ref={ref}
        data-card={tone}
        className={cn(
          'rounded-surface',
          CARD_TONES[tone] ?? CARD_TONES.default,
          isStretch && 'flex h-full flex-col',
          interactive && 'transition-[transform,box-shadow] duration-150 ease-out hover:-translate-y-0.5 hover:shadow-popover',
          className
        )}
        {...props}
      />
    </CardContext.Provider>
  )
})
Card.displayName = 'Card'

/**
 * Card header component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Header content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex min-w-0 flex-col space-y-1.5 p-5', className)} {...props} />
))
CardHeader.displayName = 'CardHeader'

/**
 * Card title component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Title content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3 ref={ref} className={cn('text-base font-semibold text-ink', className)} {...props} />
))
CardTitle.displayName = 'CardTitle'

/**
 * Card description component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Description content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-body text-ink-2', className)} {...props} />
))
CardDescription.displayName = 'CardDescription'

/**
 * Card content component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('min-w-0 p-5 pt-0', className)} {...props} />
))
CardContent.displayName = 'CardContent'

/**
 * Card footer component.
 *
 * `hairline` opts into the card-anatomy divider (visual direction analysis
 * §1/§3.5: "hairline-divided footer with relative-time metadata"), a
 * `--color-line` top border replacing the padding-only footer for cards that
 * pair metadata with a CTA. Off by default: most footers (dialogs, forms)
 * are a button row with no metadata to separate from the body.
 *
 * Inside an `interactive` card (a flex column) the footer takes `mt-auto` and
 * pins to the bottom, so footers align across a `CardGrid` row. Everywhere
 * else a hairline footer keeps its `mt-2` gap: in block flow `mt-auto`
 * resolves to 0.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Footer content
 * @param {boolean} [props.hairline=false]
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const CardFooter = React.forwardRef(({ className, hairline = false, ...props }, ref) => {
  const { stretch } = React.useContext(CardContext)
  return (
    <div
      ref={ref}
      className={cn(
        'flex min-w-0 items-center p-5 pt-0',
        stretch ? 'mt-auto' : hairline && 'mt-2',
        hairline && 'border-t border-line pt-4',
        className
      )}
      {...props}
    />
  )
})
CardFooter.displayName = 'CardFooter'

export { Card, CardGrid, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
